import React, { useMemo, useState } from "react";
import { Card, Table, Button, Form, Row, Col, Alert } from "react-bootstrap";
import Layout from "../../layout/Layout";

const DENSITY = 7.85;

const PAKET_ICI_MAP = {
  37: 7,
  61: 9,
  91: 11,
  127: 13,
  169: 15,
  217: 17,
  271: 19,
  331: 21,
  397: 23,
};

const DEFAULT_LIMITS = {
  maxTonLimit: 25000,
  maxBoyLimit: 12000,
};

const createEmptyRow = () => ({
  id: Date.now() + Math.random(),
  kalinlik: "",
  ebat: "",
  boy: "",
  paketIci: "",
  bantEni: "",
  adet: "",
  enAdet: 5,
  boyAdet: 5,
});

function toNum(value) {
  if (value === "" || value === null || value === undefined) return 0;
  const normalized = String(value).replace(",", ".");
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function round(value, digits = 2) {
  const factor = 10 ** digits;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

function formatTr(value, digits = 0) {
  if (!Number.isFinite(value)) return "";
  return value.toLocaleString("tr-TR", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

function calculateBaseRow(row) {
  const kalinlik = toNum(row.kalinlik);
  const ebat = toNum(row.ebat);
  const boy = toNum(row.boy);
  const paketIci = toNum(row.paketIci);
  const bantEni = toNum(row.bantEni);
  const adet = toNum(row.adet);
  const enAdet = toNum(row.enAdet) || 5;
  const boyAdet = toNum(row.boyAdet) || 5;

  const paketKgRaw =
    kalinlik > 0 && bantEni > 0 && boy > 0 && paketIci > 0
      ? (DENSITY * kalinlik * bantEni * boy * paketIci) / 1000000
      : 0;

  const paketKg = round(paketKgRaw, 0);
  const paketSayisiRaw = paketIci > 0 ? adet / paketIci : 0;
  const paketSayisi = Math.floor(paketSayisiRaw);
  const kalanPaket = paketIci > 0 ? adet - paketSayisi * paketIci : 0;
  const totalTons = round((paketKgRaw * paketSayisi) / 1000, 1);

  const katsayi = PAKET_ICI_MAP[paketIci] || 0;
  const paketEni = katsayi > 0 ? round(ebat * katsayi, 0) : 0;
  const paketBoyu = paketEni > 0 ? round(paketEni * 0.877, 0) : 0;
  const enToplam = paketEni > 0 ? round(paketEni * enAdet, 0) : 0;
  const boyToplam = paketBoyu > 0 ? round(paketBoyu * boyAdet, 0) : 0;

  return {
    ...row,
    _base: {
      kalinlik,
      ebat,
      boy,
      paketIci,
      bantEni,
      adet,
      enAdet,
      boyAdet,
      paketKgRaw,
      paketKg,
      paketSayisiRaw,
      paketSayisi,
      kalanPaket,
      totalTons,
      katsayi,
      paketEni,
      paketBoyu,
      enToplam,
      boyToplam,
    },
  };
}

// ─── ALGORİTMA ────────────────────────────────────────────────────────────────
// Kural 1: Ton kontrolü → konteyner dolu mu?
// Kural 2: Boy kontrolü → paketin KENDİ boyu maxBoyLimit'i aşıyor mu?
//          Birikimli toplama YOK (paketler üst üste dizilir).
// Strateji: First Fit Decreasing — büyükten küçüğe sırala, en az dolu konteynere koy.

function allocateContainersMinimized(rows, limits) {
  const allocationsByRow = {};

  for (const row of rows) {
    allocationsByRow[row.id] = {};
  }

  const preparedRows = rows.map((row) => {
    const base = row._base || {};

    const paketSayisi = Number.isFinite(base.paketSayisi)
      ? base.paketSayisi
      : 0;

    const paketKgRaw = Number.isFinite(base.paketKgRaw) ? base.paketKgRaw : 0;

    return {
      rowId: row.id,
      paketSayisi,
      paketKgRaw,
      totalKgRaw: paketSayisi * paketKgRaw,
    };
  });

  const totalKg = preparedRows.reduce((sum, row) => sum + row.totalKgRaw, 0);

  const minContainerByTon =
    totalKg > 0 ? Math.ceil(totalKg / limits.maxTonLimit) : 0;

  const initialContainerCount = Math.max(1, minContainerByTon);

  const containers = Array.from({ length: initialContainerCount }, (_, i) => ({
    no: i + 1,
    usedKgRaw: 0,
    allocations: {},
  }));

  let containerIndex = 0;

  for (const row of preparedRows) {
    const { rowId, paketSayisi, paketKgRaw } = row;

    if (paketSayisi <= 0 || paketKgRaw <= 0) continue;

    let remainingPackages = paketSayisi;

    while (remainingPackages > 0) {
      if (!containers[containerIndex]) {
        containers.push({
          no: containers.length + 1,
          usedKgRaw: 0,
          allocations: {},
        });
      }

      const currentContainer = containers[containerIndex];
      const remainingKgCapacity =
        limits.maxTonLimit - currentContainer.usedKgRaw;

      const fitPackages = Math.floor(remainingKgCapacity / paketKgRaw);

      if (fitPackages <= 0) {
        containerIndex += 1;
        continue;
      }

      const packagesToLoad = Math.min(remainingPackages, fitPackages);
      const loadKgRaw = packagesToLoad * paketKgRaw;

      const existing = currentContainer.allocations[rowId] || {
        packages: 0,
        kgRaw: 0,
      };

      existing.packages += packagesToLoad;
      existing.kgRaw += loadKgRaw;

      currentContainer.allocations[rowId] = existing;
      currentContainer.usedKgRaw += loadKgRaw;

      remainingPackages -= packagesToLoad;

      if (remainingPackages > 0) {
        containerIndex += 1;
      }
    }
  }

  for (const container of containers) {
    for (const [rowId, allocation] of Object.entries(container.allocations)) {
      allocationsByRow[rowId][container.no] = {
        packages: allocation.packages,
        kg: round(allocation.kgRaw, 0),
      };
    }
  }

  const containerUsedKgTotals = {};
  for (const container of containers) {
    containerUsedKgTotals[container.no] = round(container.usedKgRaw, 0);
  }

  return {
    containers: containers.map((container) => ({
      ...container,
      usedKg: round(container.usedKgRaw, 0),
    })),
    allocationsByRow,
    meta: {
      totalKg: round(totalKg, 0),
      totalBoy: 0,
      minContainerByTon: initialContainerCount,
      minContainerByBoy: 0,
      initialContainerCount,
      finalContainerCount: containers.length,
      containerUsedKgTotals,
    },
  };
}
// ──────────────────────────────────────────────────────────────────────────────

function KonteynerPlanlama() {
  const [rows, setRows] = useState([createEmptyRow()]);
  const [limits, setLimits] = useState(DEFAULT_LIMITS);

  const baseRows = useMemo(() => rows.map(calculateBaseRow), [rows]);

  const allocationResult = useMemo(
    () => allocateContainersMinimized(baseRows, limits),
    [baseRows, limits]
  );

  const finalRows = useMemo(() => {
    const { allocationsByRow } = allocationResult;
    return baseRows.map((row) => ({
      ...row,
      _allocations: allocationsByRow[row.id] || {},
    }));
  }, [baseRows, allocationResult]);

  const maxContainerCount = useMemo(
    () => allocationResult.containers.length,
    [allocationResult]
  );

  const totals = useMemo(() => {
    const result = { totalTons: 0, containerKgTotals: {} };
    for (const row of finalRows) {
      result.totalTons += row._base.totalTons;
      for (let i = 1; i <= maxContainerCount; i++) {
        const allocation = row._allocations[i];
        if (allocation) {
          result.containerKgTotals[i] =
            (result.containerKgTotals[i] || 0) + allocation.kg;
        }
      }
    }
    result.totalTons = round(result.totalTons, 1);
    return result;
  }, [finalRows, maxContainerCount]);

  const updateRow = (id, field, value) =>
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );

  const addEmptyRow = () => setRows((prev) => [...prev, createEmptyRow()]);

  const copyRow = (id) =>
    setRows((prev) => {
      const source = prev.find((r) => r.id === id);
      if (!source) return prev;
      return [...prev, { ...source, id: Date.now() + Math.random() }];
    });

  const deleteRow = (id) =>
    setRows((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((r) => r.id !== id);
    });

  const renderContainerHeaders = () => {
    const headers = [];
    for (let i = 1; i <= maxContainerCount; i++) {
      headers.push(<th key={`ch-${i}`}>{i}. Konteyner</th>);
      headers.push(<th key={`ck-${i}`}>Kg</th>);
    }
    return headers;
  };

  const renderContainerCells = (row) => {
    const cells = [];
    for (let i = 1; i <= maxContainerCount; i++) {
      const a = row._allocations[i];
      cells.push(
        <td key={`rc-${row.id}-${i}`}>{a ? formatTr(a.packages, 0) : ""}</td>
      );
      cells.push(
        <td key={`rk-${row.id}-${i}`}>{a ? formatTr(a.kg, 0) : ""}</td>
      );
    }
    return cells;
  };

  const renderTotalContainerCells = () => {
    const cells = [];
    for (let i = 1; i <= maxContainerCount; i++) {
      cells.push(<td key={`te-${i}`}></td>);
      cells.push(
        <td key={`tk-${i}`}>{formatTr(totals.containerKgTotals[i] || 0, 0)}</td>
      );
    }
    return cells;
  };

  return (
    <Layout>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="fw-bold m-0">Konteyner Planlama</h3>
          <small className="text-muted">
            Minimum konteyner hedefiyle karışık ürün yükleme
          </small>
        </div>
        <Button variant="primary" onClick={addEmptyRow}>
          + Boş Satır
        </Button>
      </div>

      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <Form.Label className="small fw-bold">
                Max Tonaj Limit (kg)
              </Form.Label>
              <Form.Control
                type="number"
                value={limits.maxTonLimit}
                onChange={(e) =>
                  setLimits((prev) => ({
                    ...prev,
                    maxTonLimit: toNum(e.target.value),
                  }))
                }
              />
            </Col>
            <Col md={4}>
              <Form.Label className="small fw-bold">
                Max Boy Limit (mm)
              </Form.Label>
              <Form.Control
                type="number"
                value={limits.maxBoyLimit}
                onChange={(e) =>
                  setLimits((prev) => ({
                    ...prev,
                    maxBoyLimit: toNum(e.target.value),
                  }))
                }
              />
            </Col>
            <Col md={4}>
              <Form.Label className="small fw-bold">
                Minimum Konteyner (Ton Bazlı)
              </Form.Label>
              <Form.Control
                type="text"
                value={allocationResult.meta.minContainerByTon}
                disabled
              />
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="mb-3 border-0 shadow-sm">
        <Card.Body>
          <Row className="g-3">
            <Col md={4}>
              <div className="small text-muted">Toplam Kg</div>
              <div className="fw-bold">
                {formatTr(allocationResult.meta.totalKg || 0, 0)}
              </div>
            </Col>
            <Col md={4}>
              <div className="small text-muted">Tonaja Göre Min. Konteyner</div>
              <div className="fw-bold">
                {formatTr(allocationResult.meta.minContainerByTon || 0, 0)}
              </div>
            </Col>
            <Col md={4}>
              <div className="small text-muted">Kullanılan Konteyner</div>
              <div className="fw-bold">
                {formatTr(allocationResult.meta.finalContainerCount || 0, 0)}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm">
        <Card.Body className="p-0">
          <div className="table-responsive">
            <Table
              bordered
              striped
              hover
              size="sm"
              className="mb-0 align-middle"
            >
              <thead className="table-light">
                <tr>
                  <th>KALINLIK</th>
                  <th>EBAT</th>
                  <th>BOY</th>
                  <th>PAKET İÇİ</th>
                  <th>BANT ENİ</th>
                  <th>PAKET KG</th>
                  <th>ADET</th>
                  <th>PAKET SAYISI</th>
                  <th>KALAN PAKET</th>
                  <th>TOTAL TONS</th>
                  {renderContainerHeaders()}
                  <th>paket eni</th>
                  <th>paket boyu</th>
                  <th>en adet</th>
                  <th>en toplam</th>
                  <th>boy adet</th>
                  <th>boy toplam</th>
                  <th>Uyarı</th>
                  <th>İşlem</th>
                </tr>
              </thead>
              <tbody>
                {finalRows.map((row) => (
                  <tr key={row.id}>
                    <td style={{ minWidth: 90 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={row.kalinlik}
                        onChange={(e) =>
                          updateRow(row.id, "kalinlik", e.target.value)
                        }
                      />
                    </td>
                    <td style={{ minWidth: 90 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={row.ebat}
                        onChange={(e) =>
                          updateRow(row.id, "ebat", e.target.value)
                        }
                      />
                    </td>
                    <td style={{ minWidth: 100 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={row.boy}
                        onChange={(e) =>
                          updateRow(row.id, "boy", e.target.value)
                        }
                      />
                    </td>
                    <td style={{ minWidth: 100 }}>
                      <Form.Select
                        size="sm"
                        value={row.paketIci}
                        onChange={(e) =>
                          updateRow(row.id, "paketIci", e.target.value)
                        }
                      >
                        <option value=""></option>
                        {Object.keys(PAKET_ICI_MAP).map((key) => (
                          <option key={key} value={key}>
                            {key}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                    <td style={{ minWidth: 100 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        step="0.01"
                        value={row.bantEni}
                        onChange={(e) =>
                          updateRow(row.id, "bantEni", e.target.value)
                        }
                      />
                    </td>
                    <td>{formatTr(row._base.paketKg, 0)}</td>
                    <td style={{ minWidth: 100 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={row.adet}
                        onChange={(e) =>
                          updateRow(row.id, "adet", e.target.value)
                        }
                      />
                    </td>
                    <td>{formatTr(row._base.paketSayisi, 1)}</td>
                    <td>{formatTr(row._base.kalanPaket, 0)}</td>
                    <td>{formatTr(row._base.totalTons, 1)}</td>
                    {renderContainerCells(row)}
                    <td>{formatTr(row._base.paketEni, 0)}</td>
                    <td>{formatTr(row._base.paketBoyu, 0)}</td>
                    <td style={{ minWidth: 80 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={row.enAdet}
                        onChange={(e) =>
                          updateRow(row.id, "enAdet", e.target.value)
                        }
                      />
                    </td>
                    <td>{formatTr(row._base.enToplam, 0)}</td>
                    <td style={{ minWidth: 80 }}>
                      <Form.Control
                        size="sm"
                        type="number"
                        value={row.boyAdet}
                        onChange={(e) =>
                          updateRow(row.id, "boyAdet", e.target.value)
                        }
                      />
                    </td>
                    <td>{formatTr(row._base.boyToplam, 0)}</td>
                    <td style={{ minWidth: 220 }}>
                      {row._allocations._error ? (
                        <Alert variant="danger" className="py-1 px-2 m-0">
                          {row._allocations._error}
                        </Alert>
                      ) : (
                        ""
                      )}
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          variant="outline-primary"
                          size="sm"
                          onClick={() => copyRow(row.id)}
                          title="Satırı kopyala"
                        >
                          +
                        </Button>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          onClick={() => deleteRow(row.id)}
                          title="Satırı sil"
                        >
                          -
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
                <tr className="table-secondary fw-bold">
                  <td colSpan="9" className="text-end">
                    Toplam
                  </td>
                  <td>{formatTr(totals.totalTons, 1)}</td>
                  {renderTotalContainerCells()}
                  <td colSpan="8"></td>
                </tr>
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </Layout>
  );
}

export default KonteynerPlanlama;
